
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, Phone, AlertTriangle, Pill, FileText, Clock, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MedicalInfo {
  patientName: string;
  dateOfBirth?: string;
  medications: Array<{
    name: string;
    dosage?: string;
  }>;
  conditions: Array<{
    name: string;
    status?: string;
  }>;
  allergies: Array<{
    substance: string;
    severity?: string;
  }>;
  emergencyContact?: {
    name: string;
    phone: string;
  };
}

const MedicalInfo = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { toast } = useToast();
  const [medicalInfo, setMedicalInfo] = useState<MedicalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profileId) {
      fetchMedicalProfile(profileId);
    }
  }, [profileId]);

  const fetchMedicalProfile = async (id: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/public_profile/${id}`);
      
      if (!response.ok) {
        throw new Error('Profile not found');
      }
      
      const data = await response.json();
      
      // Parse the medical data JSON string
      const medicalData = typeof data.medicalData === 'string' 
        ? JSON.parse(data.medicalData) 
        : data.medicalData;
      
      setMedicalInfo({
        patientName: data.patientName,
        dateOfBirth: data.dateOfBirth,
        medications: medicalData.medications || [],
        conditions: medicalData.conditions || [],
        allergies: medicalData.allergies || [],
        emergencyContact: medicalData.emergencyContact
      });
    } catch (err) {
      console.error('Error fetching medical profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
    toast({
      title: "Calling Emergency Contact",
      description: `Dialing ${phone}...`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center shadow-lg">
          <Loader2 className="h-16 w-16 text-red-600 mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-bold mb-4">Loading Medical Information</h1>
          <p className="text-muted-foreground">
            Please wait while we retrieve the medical profile...
          </p>
        </Card>
      </div>
    );
  }

  if (error || !medicalInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center shadow-lg">
          <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Profile Not Found</h1>
          <p className="text-muted-foreground">
            {error || 'The requested medical profile could not be found or is no longer available.'}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      {/* Header */}
      <div className="bg-red-600 text-white py-6 px-4 text-center">
        <Heart className="h-8 w-8 mx-auto mb-2" />
        <h1 className="text-2xl font-bold mb-1">EMERGENCY MEDICAL INFO</h1>
        <p className="text-red-100 text-sm">For Emergency Use Only</p>
        <p className="text-red-100 text-lg font-semibold mt-2">{medicalInfo.patientName}</p>
        {medicalInfo.dateOfBirth && (
          <p className="text-red-100 text-sm">DOB: {medicalInfo.dateOfBirth}</p>
        )}
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Emergency Contact - Most Prominent */}
        {medicalInfo.emergencyContact && (
          <Card className="bg-white shadow-lg border-red-200">
            <div className="p-6 text-center">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Phone className="h-6 w-6 text-red-600" />
                <h2 className="text-xl font-bold text-red-800">EMERGENCY CONTACT</h2>
              </div>
              <div className="mb-4">
                <p className="text-2xl font-bold text-gray-900">{medicalInfo.emergencyContact.name}</p>
                <p className="text-xl text-gray-700 font-semibold">{medicalInfo.emergencyContact.phone}</p>
              </div>
              <Button 
                size="lg"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 text-lg"
                onClick={() => handleEmergencyCall(medicalInfo.emergencyContact!.phone)}
              >
                <Phone className="h-5 w-5 mr-2" />
                CALL NOW
              </Button>
            </div>
          </Card>
        )}

        {/* Critical Allergies */}
        {medicalInfo.allergies.length > 0 && !medicalInfo.allergies.some(a => a.substance === 'No Known Allergies') && (
          <Card className="bg-red-50 border-2 border-red-300 shadow-lg">
            <div className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <h2 className="text-xl font-bold text-red-800">⚠️ CRITICAL ALLERGIES</h2>
              </div>
              <div className="space-y-2">
                {medicalInfo.allergies.map((allergy, index) => (
                  allergy.substance !== 'No Known Allergies' && (
                    <div key={index} className="bg-red-100 border border-red-300 rounded-lg p-3">
                      <span className="text-red-800 font-semibold text-lg">{allergy.substance}</span>
                      {allergy.severity && (
                        <span className="text-red-600 text-sm ml-2">({allergy.severity})</span>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Current Medications */}
        {medicalInfo.medications.length > 0 && (
          <Card className="bg-white shadow-lg">
            <div className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Pill className="h-6 w-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-800">Current Medications</h2>
              </div>
              <div className="space-y-2">
                {medicalInfo.medications.map((medication, index) => (
                  <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <span className="text-blue-800 font-medium">{medication.name}</span>
                    {medication.dosage && (
                      <p className="text-blue-600 text-sm mt-1">{medication.dosage}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Medical Conditions */}
        {medicalInfo.conditions.length > 0 && (
          <Card className="bg-white shadow-lg">
            <div className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="h-6 w-6 text-orange-600" />
                <h2 className="text-xl font-bold text-gray-800">Medical Conditions</h2>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {medicalInfo.conditions.map((condition, index) => (
                  <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <span className="text-orange-800 font-medium">{condition.name}</span>
                    {condition.status && (
                      <span className="text-orange-600 text-sm ml-2">({condition.status})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Footer */}
        <Card className="bg-gray-50 shadow-sm">
          <div className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <p className="text-sm text-gray-600">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Generated by HealthScore Emergency Medical System
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MedicalInfo;
